import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, AlertCircle, HelpCircle, Coins, Sparkles, Zap, Trophy, ChevronLeft, ChevronRight, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Tooltip } from '@/components/ui/tooltip';
import { ParticleBackground } from './animations/ParticleBackground';
import { SectionWrapper } from './shared/SectionWrapper';
import { SectionTransition } from '@/components/shared/SectionTransition';
import { HoverEffect } from '@/components/animations/HoverEffect';
import { FloatingAnimation } from '@/components/animations/FloatingAnimation';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { paths } from "@/config/paths"; // Make sure this exists or we'll create it

interface PathSelectionProps {
  plan: string;
  isOpen: boolean;
  onClose: () => void;
}

const PathSelectionModal = ({ plan, isOpen, onClose }: PathSelectionProps) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-slate-900/95 backdrop-blur-xl border-white/10 max-w-4xl">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold bg-gradient-to-r from-primary-400 to-primary-600 bg-clip-text text-transparent">
            Choose Your Career Path - {plan} Plan
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
          {paths.map((path, index) => (
            <motion.div
              key={path.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="group cursor-pointer"
              onClick={() => {
                // Handle path selection
                console.log(`Selected ${path.title} with ${plan} plan`);
                onClose();
              }}
            >
              <div className="p-4 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 
                            transition-all duration-300 hover:border-primary-400/50">
                <div className="flex items-center gap-3 mb-3">
                  {path.icon}
                  <h3 className="text-lg font-semibold text-white">{path.title}</h3>
                </div>
                <p className="text-sm text-gray-400">{path.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Add interactive price display
const PriceDisplay = ({ price, period, isPopular }: { price: number; period: string; isPopular: boolean }) => {
  return (
    <motion.div
      className="relative"
      whileHover={{ scale: 1.05 }}
      transition={{ type: "spring", stiffness: 300 }}
    >
      <div className="flex items-baseline justify-center">
        <span className="text-2xl text-gray-400 mr-1">R</span>
        <motion.span 
          className={`text-6xl font-bold ${isPopular ? 'text-primary-400' : 'text-white'}`}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {price}
        </motion.span>
        <span className="text-gray-400 ml-2">{period}</span>
      </div>
      {isPopular && (
        <motion.div
          className="absolute -inset-4 rounded-xl bg-primary-400/20 -z-10"
          animate={{
            scale: [1, 1.05, 1],
            opacity: [0.5, 0.8, 0.5],
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      )}
    </motion.div>
  );
};

const AnimatedButtonText = ({ text, isPopular }: { text: string; isPopular: boolean }) => {
  return (
    <motion.span 
      className="relative flex items-center justify-center w-full"
      whileHover={{ scale: 1.05 }}
    >
      <motion.span
        initial={false}
        animate={{ y: [-1, 1] }}
        transition={{
          duration: 1.5,
          repeat: Infinity,
          repeatType: "reverse",
        }}
        className={`flex items-center gap-2 ${isPopular ? 'text-white' : 'text-primary-400'}`}
      >
        {text}
        <motion.span
          initial={{ x: -3 }}
          animate={{ x: 3 }}
          transition={{
            duration: 1.5,
            repeat: Infinity,
            repeatType: "reverse",
          }}
        >
          →
        </motion.span>
      </motion.span>
      {isPopular && (
        <motion.span
          className="absolute inset-0 bg-primary-400/20 blur-sm rounded-full"
          initial={{ opacity: 0, scale: 0.8 }}
          whileHover={{ 
            opacity: [0, 0.2, 0],
            scale: [0.8, 1.2, 0.8],
            transition: { 
              duration: 1.5,
              repeat: Infinity,
            }
          }}
        />
      )}
    </motion.span>
  );
};

export function Pricing() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isAnnual, setIsAnnual] = useState(false);
  const [hoveredFeature, setHoveredFeature] = useState<string | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);

  const features: PricingFeature[] = [
    {
      name: "Basic AI Career Assessment",
      free: true,
      pro: true,
      premium: true
    },
    {
      name: "Monthly Career Coins",
      free: "50 coins",
      pro: "200 coins",
      premium: "500 coins",
      highlight: true,
      tooltip: "Spend coins on AI reviews, mock interviews, and premium features"
    },
    {
      name: "AI Career Coach Access",
      free: "5 coins/session",
      pro: "2 coins/session",
      premium: "Unlimited",
      tooltip: "24/7 AI-powered career guidance"
    },
    {
      name: "Job Matching",
      free: "10 coins/match",
      pro: "5 coins/match",
      premium: "Unlimited"
    },
    {
      name: "CV Reviews",
      free: "15 coins/review",
      pro: "10 coins/review",
      premium: "2/month free"
    },
    {
      name: "Mock Interviews",
      free: "25 coins/session",
      pro: "15 coins/session",
      premium: "1/month free"
    },
    {
      name: "Daily Bonus Coins",
      free: "5 coins",
      pro: "15 coins",
      premium: "30 coins",
      highlight: true
    },
    {
      name: "Career Strategy Session",
      free: "50 coins/session",
      pro: "25 coins/session",
      premium: "Quarterly (Free)",
      tooltip: "1-on-1 session with career expert"
    }
  ];

  // Define plans array
  const plans = [
    {
      name: "Free",
      description: "Start your journey with basic features",
      price: 0,
      period: "",
      coinBonus: "50 coins to start",
      features: features.filter(f => f.free),
      cta: "Start Free Trial",
      popular: false,
      icon: <Coins className="h-8 w-8 text-yellow-400" />,
      gradient: "from-yellow-400/20 to-orange-500/20",
      color: "yellow"
    },
    {
      name: "Monthly Pro",
      description: "Accelerate your career growth",
      price: 149,
      period: "/month",
      coinBonus: "200 coins monthly",
      features: features.filter(f => f.pro),
      cta: "Get Pro Access",
      popular: true,
      icon: <Zap className="h-8 w-8 text-primary-400" />,
      gradient: "from-primary-400/20 to-violet-500/20",
      color: "primary"
    },
    {
      name: "Yearly Premium",
      description: "Maximum value, ultimate features",
      price: 1500,
      period: "/year",
      coinBonus: "500 coins monthly",
      features: features.filter(f => f.premium),
      cta: "Go Premium",
      popular: false,
      icon: <Trophy className="h-8 w-8 text-amber-400" />,
      gradient: "from-amber-400/20 to-red-500/20",
      color: "amber"
    }
  ] as const;

  return (
    <SectionWrapper id="pricing" className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <div className="absolute inset-0 z-0">
        <ParticleBackground 
          color="#06b6d4"
          density={40}
          speed={0.3}
          connectParticles={true}
          connectionDistance={160}
          connectionOpacity={0.2}
        />
      </div>

      <SectionTransition direction="up">
        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          {/* Header Section */}
          <div className="text-center mb-16">
            <motion.div 
              className="inline-flex items-center justify-center space-x-2 mb-4"
              initial={{ opacity: 0, scale: 0.5 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
            >
              <Sparkles className="h-6 w-6 text-primary-400" />
              <span className="text-primary-400 font-medium">Simple Pricing</span>
              <Sparkles className="h-6 w-6 text-primary-400" />
            </motion.div>
            
            <motion.h2 
              className="text-3xl md:text-4xl font-bold mb-4"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              Choose Your{' '}
              <span className="bg-gradient-to-r from-primary-400 to-primary-600 bg-clip-text text-transparent">
                Career Path
              </span>
            </motion.h2>
            
            <motion.div 
              className="text-gray-400 text-lg max-w-2xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              Start with our free plan or upgrade for premium features
            </motion.div>
          </div>

          {/* Carousel Navigation */}
          <div className="flex items-center justify-center gap-4 mb-12">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setActiveIndex((current) => (current - 1 + 3) % 3)}
              className="text-gray-400 hover:text-white"
            >
              <ChevronLeft className="h-6 w-6" />
            </Button>
            
            {[0, 1, 2].map((index) => (
              <motion.button
                key={index}
                className={`w-3 h-3 rounded-full ${
                  activeIndex === index 
                    ? 'bg-primary-400' 
                    : 'bg-gray-600'
                }`}
                whileHover={{ scale: 1.2 }}
                onClick={() => setActiveIndex(index)}
              />
            ))}

            <Button
              variant="ghost"
              size="icon"
              onClick={() => setActiveIndex((current) => (current + 1) % 3)}
              className="text-gray-400 hover:text-white"
            >
              <ChevronRight className="h-6 w-6" />
            </Button>
          </div>

          {/* Plans Carousel */}
          <div className="relative overflow-hidden">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeIndex}
                className="flex justify-center"
                initial={{ opacity: 0, x: 100 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -100 }}
                transition={{ duration: 0.5 }}
              >
                <div className="w-full max-w-lg">
                  <HoverEffect glowColor={
                    activeIndex === 0 
                      ? "rgba(234, 179, 8, 0.3)"
                      : activeIndex === 1
                        ? "rgba(168, 85, 247, 0.3)"
                        : "rgba(251, 191, 36, 0.3)"
                  }>
                    <div className="p-8 rounded-xl bg-white/5 backdrop-blur-sm border border-white/10">
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.2 }}
                        className="relative"
                      >
                        {/* Plan Header */}
                        <div className="flex items-center justify-between mb-6">
                          <div>
                            <h3 className="text-2xl font-bold text-white">
                              {plans[activeIndex].name}
                            </h3>
                            <p className="text-gray-400">{plans[activeIndex].description}</p>
                          </div>
                          <FloatingAnimation>
                            {plans[activeIndex].icon}
                          </FloatingAnimation>
                        </div>

                        {/* Price Display */}
                        <PriceDisplay 
                          price={plans[activeIndex].price}
                          period={plans[activeIndex].period}
                          isPopular={plans[activeIndex].popular}
                        />

                        {/* Coin Bonus */}
                        <motion.div
                          className="mt-4 p-3 rounded-lg bg-yellow-400/10 border border-yellow-400/20"
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.3 }}
                        >
                          <div className="flex items-center gap-2">
                            <Coins className="h-5 w-5 text-yellow-400" />
                            <span className="text-yellow-400 font-medium">
                              {plans[activeIndex].coinBonus}
                            </span>
                          </div>
                        </motion.div>

                        {/* Features List */}
                        <motion.ul className="mt-6 space-y-4">
                          {features.map((feature, i) => {
                            const value = activeIndex === 0 
                              ? feature.free 
                              : activeIndex === 1
                                ? feature.pro
                                : feature.premium;

                            if (!value) return null;

                            return (
                              <motion.li
                                key={feature.name}
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: i * 0.1 }}
                                className="flex items-start gap-3"
                                onMouseEnter={() => setHoveredFeature(feature.name)}
                                onMouseLeave={() => setHoveredFeature(null)}
                              >
                                <Check className={`h-5 w-5 flex-shrink-0 ${
                                  feature.highlight ? 'text-primary-400' : 'text-gray-400'
                                }`} />
                                <div>
                                  <span className={`text-sm ${
                                    feature.highlight ? 'text-white' : 'text-gray-300'
                                  }`}>
                                    {feature.name}
                                  </span>
                                  {typeof value === 'string' && (
                                    <span className="ml-1 text-primary-400">
                                      ({value})
                                    </span>
                                  )}
                                  {feature.tooltip && (
                                    <Tooltip content={feature.tooltip}>
                                      <HelpCircle className="inline-block ml-1 h-4 w-4 text-gray-400" />
                                    </Tooltip>
                                  )}
                                </div>
                              </motion.li>
                            );
                          })}
                        </motion.ul>

                        {/* CTA Button */}
                        <motion.div className="mt-8">
                          <Button 
                            className={`w-full ${
                              plans[activeIndex].popular 
                                ? 'bg-primary-400 hover:bg-primary-500' 
                                : 'bg-white/10 hover:bg-white/20'
                            } text-white`}
                            onClick={() => setSelectedPlan(plans[activeIndex].name)}
                          >
                            <AnimatedButtonText 
                              text={plans[activeIndex].cta} 
                              isPopular={plans[activeIndex].popular} 
                            />
                          </Button>
                        </motion.div>
                      </motion.div>
                    </div>
                  </HoverEffect>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </SectionTransition>

      {/* Path Selection Modal */}
      <PathSelectionModal
        plan={selectedPlan || ''}
        isOpen={!!selectedPlan}
        onClose={() => setSelectedPlan(null)}
      />
    </SectionWrapper>
  );
}